/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React from "react"
import PropTypes from "prop-types"
import { Helmet } from "react-helmet"
import { useStaticQuery, graphql, withPrefix } from "gatsby"
import { useLocation } from "@gatsbyjs/reach-router"
import { useLocalization } from "gatsby-theme-i18n"

const privatePathPrefixes = [
  `/login`,
  `/my_identity`,
  `/exchange_deposit`,
  `/exchange_order_history`,
  `/wallet_balances`,
  `/wallet_receive`,
  `/wallet_send`,
  `/wallet_transactions`,
]

const stripLocalePrefix = (pathname, locales) => {
  const matchingLocale = locales.find(
    ({ code }) => pathname === `/${code}` || pathname.startsWith(`/${code}/`)
  )

  if (!matchingLocale) {
    return pathname
  }

  const strippedPath = pathname.replace(`/${matchingLocale.code}`, ``)
  return strippedPath || `/`
}

const normalizePath = path => {
  if (!path || path === `/`) {
    return `/`
  }

  return path.endsWith(`/`) ? path.slice(0, -1) : path
}

const absoluteUrl = (siteUrl, path) =>
  `${siteUrl}${path === `/` ? `` : withPrefix(path)}`

function SEO({ description, lang, meta, title }) {
  const location = useLocation()
  const { locale, config, defaultLang, prefixDefault, localizedPath } =
    useLocalization()
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
            siteUrl
          }
        }
      }
    `
  )

  const metaDescription = description || site.siteMetadata.description
  const siteUrl = site.siteMetadata.siteUrl.replace(/\/$/, ``)
  const pathname = normalizePath(location.pathname)
  const originalPath = normalizePath(stripLocalePrefix(pathname, config))
  const currentLocale = config.find(({ code }) => code === locale)
  const hrefLang = currentLocale ? currentLocale.hrefLang : lang
  const canonicalPath = localizedPath({
    defaultLang,
    prefixDefault,
    locale,
    path: originalPath,
  })
  const canonicalUrl = absoluteUrl(siteUrl, normalizePath(canonicalPath))
  const isPrivatePath = privatePathPrefixes.some(
    pathPrefix =>
      originalPath === pathPrefix || originalPath.startsWith(`${pathPrefix}/`)
  )
  const imageUrl = `${siteUrl}/belgacoin.png`
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Belgacoin",
    url: siteUrl,
    logo: imageUrl,
    sameAs: ["https://twitter.com/Belgacoin"],
  }
  const websiteData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: site.siteMetadata.title,
    url: siteUrl,
    inLanguage: hrefLang,
  }

  return (
    <Helmet
      htmlAttributes={{
        lang: hrefLang,
      }}
      title={title}
      titleTemplate={`%s | ${site.siteMetadata.title}`}
      link={[
        {
          rel: `canonical`,
          href: canonicalUrl,
        },
      ]}
      meta={[
        {
          name: `robots`,
          content: isPrivatePath
            ? `noindex, nofollow`
            : `index, follow, max-image-preview:large`,
        },
        {
          name: `description`,
          content: metaDescription,
        },
        {
          property: `og:title`,
          content: title,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          property: `og:url`,
          content: canonicalUrl,
        },
        {
          property: `og:image`,
          content: imageUrl,
        },
        {
          property: `og:locale`,
          content: hrefLang.replace(`-`, `_`),
        },
        {
          property: `og:type`,
          content: `website`,
        },
        {
          name: `twitter:card`,
          content: `summary`,
        },
        {
          name: `twitter:creator`,
          content: site.siteMetadata.author,
        },
        {
          name: `twitter:title`,
          content: title,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
        {
          name: `twitter:image`,
          content: imageUrl,
        },
      ].concat(meta)}
    >
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      <script type="application/ld+json">{JSON.stringify(websiteData)}</script>
    </Helmet>
  )
}

SEO.defaultProps = {
  lang: `en`,
  meta: [],
  description: ``,
}

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
}

export default SEO
